// src/components/BoardPack/ExportOverlay.jsx
// Rendered once at the App level so export progress stays visible (and
// controllable) across route changes, independent of whether BoardPack's
// route is currently mounted.
import { Loader2, Minus } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Progress } from "../ui/progress";
import { useExport } from "@/context/ExportContext";

const ExportProgressDialog = ({
  open,
  progress,
  generatingLabel,
  doneLabel,
  descriptionInProgress,
  descriptionDone,
  onMinimize,
  onCancel,
}) => (
  <Dialog open={open} onOpenChange={() => {}}>
    <DialogContent className="sm:max-w-md" showCloseButton={false}>
      <DialogHeader>
        <DialogTitle className="flex items-center justify-between gap-2.5 text-lg">
          <span className="flex items-center gap-2.5">
            {progress < 100 ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> {generatingLabel} — {progress}%
              </>
            ) : (
              doneLabel
            )}
          </span>
          {progress < 100 && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMinimize}>
              <Minus className="h-4 w-4" />
            </Button>
          )}
        </DialogTitle>
        <div className="py-3">
          <Progress value={progress} className="h-2.5" />
        </div>
        <DialogDescription className="pt-1">
          {progress < 100 ? descriptionInProgress : descriptionDone}
        </DialogDescription>
      </DialogHeader>
      {progress < 100 && (
        <div className="flex justify-end gap-2 pt-2">
           {/* <Button variant="outline" size="sm" onClick={onMinimize}>
            Minimize
          </Button> */}
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const FloatingProgressBar = ({ label, progress, onOpen, onCancel }) => (
  <div className="w-72 rounded-lg border bg-background shadow-lg p-3 pointer-events-auto">
    <div className="flex items-center gap-2 mb-2 text-sm font-medium">
      <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
      <span className="truncate">
        {label} — {progress}%
      </span>
    </div>
    <Progress value={progress} className="h-2 mb-3" />
    <div className="flex justify-end gap-2">
      <Button variant="outline" size="sm" onClick={onOpen}>
        Open
      </Button>
      <Button variant="outline" size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  </div>
);

const ExportOverlay = () => {
  const {
    pdfDialogOpen,
    pdfProgress,
    pdfMinimized,
    pptDialogOpen,
    pptProgress,
    pptMinimized,
    minimizeExport,
    restoreExport,
    cancelExport,
  } = useExport();

  return (
    <>
      <ExportProgressDialog
        open={pdfDialogOpen && !pdfMinimized}
        progress={pdfProgress}
        generatingLabel="Generating PDF"
        doneLabel="PDF Generated Successfully"
        descriptionInProgress={
          <>
            Converting pages to high-quality PDF.
            <br />
            This may time depending on page count.
          </>
        }
        descriptionDone="Your file has been generated and should start downloading automatically."
        onMinimize={() => minimizeExport("pdf")}
        onCancel={() => cancelExport("pdf")}
      />

      <ExportProgressDialog
        open={pptDialogOpen && !pptMinimized}
        progress={pptProgress}
        generatingLabel="Generating PowerPoint"
        doneLabel="PowerPoint Generated Successfully"
        descriptionInProgress={
          <>
            Converting pages to high-quality PPT.
            <br />
            This may take time depending on page count.
          </>
        }
        descriptionDone="Your .pptx file has been created and should download automatically."
        onMinimize={() => minimizeExport("ppt")}
        onCancel={() => cancelExport("ppt")}
      />

      {((pdfDialogOpen && pdfMinimized) || (pptDialogOpen && pptMinimized)) && (
        <div className="fixed bottom-6 right-6 z-[200] flex flex-col gap-3 items-end pointer-events-none">
          {pdfDialogOpen && pdfMinimized && (
            <FloatingProgressBar
              label="Generating PDF"
              progress={pdfProgress}
              onOpen={() => restoreExport("pdf")}
              onCancel={() => cancelExport("pdf")}
            />
          )}
          {pptDialogOpen && pptMinimized && (
            <FloatingProgressBar
              label="Generating PowerPoint"
              progress={pptProgress}
              onOpen={() => restoreExport("ppt")}
              onCancel={() => cancelExport("ppt")}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ExportOverlay;
